import React, { useState, useMemo, useEffect } from 'react';
import {
  FileText, ChevronDown, ChevronRight, Play, Download,
  AlertCircle, AlertTriangle, RefreshCw, X, Printer, Calendar
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { csvCell } from '../utils/csvSafe';
import { NavBar } from './NavBar';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { ExportButtons } from './ExportButtons';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FilterDef {
  key: string;
  label: string;
  type: 'year' | 'month' | 'quarter' | 'text' | 'select' | 'period_type' | 'date_range_preset';
  required?: boolean;
  staticOptions?: { value: string; label: string }[];
  dynamicSource?: 'facilities' | 'service_areas' | 'to_facilities';
  placeholder?: string;
  hideIfQuarterly?: boolean;
  showIfQuarterly?: boolean;
  showIfEquals?: { key: string; value: string };  // generic conditional visibility
}

interface TrendChartConfig {
  periodYearKey:  string;
  periodMonthKey: string;
  periodLabelKey: string;
  categoryKey:    string;
  valueKey:       string;
  colors?: Record<string, string>;
}

interface ReportDef {
  id: string;
  name: string;
  description: string;
  filters: FilterDef[];
  pivot?: 'material_code' | 'facility_name';
  rowLimitSelector?: boolean;
  quarterlyVariant?: string;
  excludeColumns?: string[];
  emptyMessage?: string;
  trendChart?: TrendChartConfig;
}

interface Category {
  id: string;
  label: string;
  color: string;
  reports: ReportDef[];
}

interface Lookups {
  facilities:   { facility_code: string; facility_name: string }[];
  serviceAreas: { service_area_code: string; service_area_name: string }[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTHS = [
  { value: '1',  label: 'January'  }, { value: '2',  label: 'February' },
  { value: '3',  label: 'March'    }, { value: '4',  label: 'April'    },
  { value: '5',  label: 'May'      }, { value: '6',  label: 'June'     },
  { value: '7',  label: 'July'     }, { value: '8',  label: 'August'   },
  { value: '9',  label: 'September'}, { value: '10', label: 'October'  },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const QUARTERS = [
  { value: '1', label: 'Q1 (Jan–Mar)' }, { value: '2', label: 'Q2 (Apr–Jun)' },
  { value: '3', label: 'Q3 (Jul–Sep)' }, { value: '4', label: 'Q4 (Oct–Dec)' },
];

const ROW_LIMIT_OPTIONS = [
  { value: '100',  label: '100 rows'   },
  { value: '500',  label: '500 rows'   },
  { value: '1000', label: '1,000 rows' },
  { value: '5000', label: '5,000 rows' },
];

const PERIOD_TYPE_OPTIONS = [
  { value: 'month',   label: 'Month'   },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year',    label: 'Year'    },
];

const DATE_RANGE_PRESETS = [
  { value: 'trailing12', label: 'Trailing 12 Months' },
  { value: 'ytd',        label: 'Year to Date' },
  { value: 'trailing6',  label: 'Trailing 6 Months' },
  { value: 'trailing3',  label: 'Trailing 3 Months' },
];

function computeDateRange(preset: string): { start_year: string; start_month: string; end_year: string; end_month: string } {
  const now = new Date();
  const endYear = now.getFullYear();
  const endMonth = now.getMonth() + 1;
  let startYear = endYear;
  let startMonth = endMonth;

  const back = (months: number) => {
    let m = endMonth - months;
    let y = endYear;
    while (m <= 0) { m += 12; y -= 1; }
    return { y, m };
  };

  if (preset === 'ytd') {
    startYear = endYear; startMonth = 1;
  } else if (preset === 'trailing6') {
    const r = back(5); startYear = r.y; startMonth = r.m;
  } else if (preset === 'trailing3') {
    const r = back(2); startYear = r.y; startMonth = r.m;
  } else {
    const r = back(11); startYear = r.y; startMonth = r.m;
  }

  return {
    start_year:  String(startYear),
    start_month: String(startMonth),
    end_year:    String(endYear),
    end_month:   String(endMonth),
  };
}

const now           = new Date();
const CURRENT_YEAR  = String(now.getFullYear());
const CURRENT_MONTH = String(now.getMonth() + 1);
const CURRENT_Q     = String(Math.ceil((now.getMonth() + 1) / 3));

// ─── Report Catalogue ─────────────────────────────────────────────────────────

const CATEGORIES: Category[] = [
  {
    id: '1', label: 'Scale Ticket Reports', color: 'blue',
    reports: [
      {
        id: '1.1', name: 'Scale Tickets Complete',
        description: 'Full ticket list with all columns for audits and customer billing.',
        rowLimitSelector: true,
        filters: [
          { key: 'year',            label: 'Year',            type: 'year',   required: true },
          { key: 'month',           label: 'Month',           type: 'month',  required: true },
          { key: 'facility',        label: 'Facility',        type: 'select', dynamicSource: 'facilities' },
          { key: 'customer_number', label: 'Customer Number', type: 'text',   placeholder: 'Optional' },
          { key: 'ticket_date',     label: 'Ticket Date',     type: 'text',   placeholder: 'YYYY-MM-DD' },
          { key: 'haul_type',       label: 'Haul Type',       type: 'select', staticOptions: [{ value: 'Direct', label: 'Direct' }, { value: 'Transfer', label: 'Transfer' }] },
        ],
      },
      {
        id: '1.2', name: 'Scale Tickets With Route Numbers',
        description: 'Route-only tickets enriched with resolved material, service area, and load type.',
        rowLimitSelector: true,
        filters: [
          { key: 'year',         label: 'Year',         type: 'year',   required: true },
          { key: 'month',        label: 'Month',        type: 'month',  required: true },
          { key: 'facility',     label: 'Facility',     type: 'select', dynamicSource: 'facilities' },
          { key: 'route_number', label: 'Route Number', type: 'text',   placeholder: 'Optional' },
          { key: 'ticket_date',  label: 'Ticket Date',  type: 'text',   placeholder: 'YYYY-MM-DD' },
          { key: 'haul_type',    label: 'Haul Type',    type: 'select', staticOptions: [{ value: 'Direct', label: 'Direct' }, { value: 'Transfer', label: 'Transfer' }] },
        ],
      },
      {
        id: '1.3', name: 'Scale Ticket Summary By Route Number',
        description: 'Ticket counts and allocated tonnage summarized by route and service area.',
        filters: [
          { key: 'year',         label: 'Year',         type: 'year',   required: true },
          { key: 'month',        label: 'Month',        type: 'month',  required: true },
          { key: 'facility',     label: 'Facility',     type: 'select', dynamicSource: 'facilities' },
          { key: 'route_number', label: 'Route Number', type: 'text',   placeholder: 'Optional' },
        ],
      },
      {
        id: '1.4', name: 'Ticket Load Weight Distribution By Service Area',
        description: 'One row per route–service area combination showing allocation percentages.',
        filters: [
          { key: 'year',         label: 'Year',         type: 'year',   required: true },
          { key: 'month',        label: 'Month',        type: 'month',  required: true },
          { key: 'facility',     label: 'Facility',     type: 'select', dynamicSource: 'facilities' },
          { key: 'route_number', label: 'Route Number', type: 'text',   placeholder: 'Optional' },
        ],
      },
      {
        id: '1.5', name: 'Summary of Tickets By Service Area With Material Type and Load Type',
        description: 'Ticket counts and tons grouped by service area, material, and load type.',
        filters: [
          { key: 'year',              label: 'Year',         type: 'year',   required: true },
          { key: 'month',             label: 'Month',        type: 'month',  required: true },
          { key: 'facility',          label: 'Facility',     type: 'select', dynamicSource: 'facilities' },
          { key: 'service_area_name', label: 'Service Area', type: 'select', dynamicSource: 'service_areas' },
        ],
      },
      {
        id: '1.6', name: 'Service Area Tonnage Summary',
        description: 'Allocated tons by service area, material grade, and load type.',
        filters: [
          { key: 'year',              label: 'Year',         type: 'year',   required: true },
          { key: 'month',             label: 'Month',        type: 'month',  required: true },
          { key: 'facility',          label: 'Facility',     type: 'select', dynamicSource: 'facilities' },
          { key: 'service_area_name', label: 'Service Area', type: 'select', dynamicSource: 'service_areas' },
        ],
      },
      {
        id: '1.7', name: 'Ticket Summary By Haul Type',
        description: 'Ticket counts and total tons grouped by haul type (Direct vs Transfer), facility, service area, and material.',
        filters: [
          { key: 'year',              label: 'Year',         type: 'year',   required: true },
          { key: 'month',             label: 'Month',        type: 'month',  required: true },
          { key: 'facility',          label: 'Facility',     type: 'select', dynamicSource: 'facilities' },
          { key: 'haul_type',         label: 'Haul Type',    type: 'select', staticOptions: [{ value: 'Direct', label: 'Direct' }, { value: 'Transfer', label: 'Transfer' }] },
          { key: 'service_area_name', label: 'Service Area', type: 'select', dynamicSource: 'service_areas' },
        ],
      },
      {
        id: '1.8', name: 'Summary of Tickets By Service Area',
        description: 'Ticket counts and allocated tons grouped by service area and material — without load type breakdown.',
        filters: [
          { key: 'year',              label: 'Year',         type: 'year',   required: true },
          { key: 'month',             label: 'Month',        type: 'month',  required: true },
          { key: 'facility',          label: 'Facility',     type: 'select', dynamicSource: 'facilities' },
          { key: 'service_area_name', label: 'Service Area', type: 'select', dynamicSource: 'service_areas' },
        ],
      },
      {
        id: '1.9', name: 'Customer Ticket Summary',
        description: 'Individual ticket detail by customer including weights, rates, and fees.',
        rowLimitSelector: true,
        filters: [
          { key: 'year',            label: 'Year',            type: 'year',   required: true },
          { key: 'month',           label: 'Month',           type: 'month',  required: true },
          { key: 'facility',        label: 'Facility',        type: 'select', dynamicSource: 'facilities' },
          { key: 'customer_number', label: 'Customer Number', type: 'text',   placeholder: 'Optional' },
          { key: 'ticket_date',     label: 'Ticket Date',     type: 'text',   placeholder: 'YYYY-MM-DD' },
          { key: 'haul_type',       label: 'Haul Type',       type: 'select', staticOptions: [{ value: 'Direct', label: 'Direct' }, { value: 'Transfer', label: 'Transfer' }] },
        ],
      },
      {
        id: '1.10', name: 'Monthly Ticket Material Summary',
        description: 'Daily ticket totals by load type and material for one facility and month, with a running weekly total alongside the daily detail.',
        filters: [
          { key: 'year',     label: 'Year',     type: 'year',   required: true },
          { key: 'month',    label: 'Month',    type: 'month',  required: true },
          { key: 'facility', label: 'Facility', type: 'select', dynamicSource: 'facilities', required: true },
        ],
      },
    ],
  },
    {
    id: '2', label: 'Facility Performance Reports', color: 'green',
    reports: [
      {
        id: '2.1', name: 'Mass Balance Complete',
        description: 'Full inbound vs outbound reconciliation with status and notes per material.',
        filters: [
          { key: 'year',     label: 'Year',     type: 'year',   required: true },
          { key: 'month',    label: 'Month',    type: 'month',  required: true },
          { key: 'facility', label: 'Facility', type: 'select', dynamicSource: 'facilities' },
        ],
      },
      {
        id: '2.2', name: 'Monthly Material Summary — All Inbound Sources',
        excludeColumns: ['facility_code', 'service_area_code'],
        description: 'Total tons by material, service area, and load type for a given period.',
        quarterlyVariant: '2.2q',
        filters: [
          { key: 'year',              label: 'Year',         type: 'year',    required: true },
          { key: 'month',             label: 'Month',        type: 'month',   required: true, hideIfQuarterly: true },
          { key: 'period_quarter',    label: 'Quarter',      type: 'quarter', required: true, showIfQuarterly: true },
          { key: 'facility',          label: 'Facility',     type: 'select',  dynamicSource: 'facilities' },
          { key: 'service_area_name', label: 'Service Area', type: 'select',  dynamicSource: 'service_areas' },
          { key: 'program', label: 'Program', type: 'select', staticOptions: [{ value: 'Residential', label: 'Residential' }, { value: 'Commercial', label: 'Commercial' }] },
        ],
      },
      {
        id: '2.3', name: 'Quarterly Inbound By Collection Method',
        excludeColumns: ['facility_code'],
        description: 'Tons by vehicle type and material group for a given quarter.',
        filters: [
          { key: 'year',           label: 'Year',     type: 'year',    required: true },
          { key: 'period_quarter', label: 'Quarter',  type: 'quarter', required: true },
          { key: 'facility',       label: 'Facility', type: 'select',  dynamicSource: 'facilities' },
          { key: 'program', label: 'Program', type: 'select', staticOptions: [{ value: 'Residential', label: 'Residential' }, { value: 'Commercial', label: 'Commercial' }] },
        ],
      },
      {
        id: '2.4', name: 'Inbound By Service Area',
        description: 'Monthly inbound tons broken down by facility, service area, and material code.',
        filters: [
          { key: 'year',              label: 'Year',         type: 'year',   required: true },
          { key: 'month',             label: 'Month',        type: 'month',  required: true },
          { key: 'facility',          label: 'Facility',     type: 'select', dynamicSource: 'facilities' },
          { key: 'service_area_name', label: 'Service Area', type: 'select', dynamicSource: 'service_areas' },
          { key: 'program', label: 'Program', type: 'select', staticOptions: [{ value: 'Residential', label: 'Residential' }, { value: 'Commercial', label: 'Commercial' }] },
        ],
      },
      {
        id: '2.5', name: 'Facility Material Input and Output Summary',
        description: 'REC-MIXED input vs recovered commodity output and residual disposal.',
        filters: [
          { key: 'year',     label: 'Year',     type: 'year',   required: true },
          { key: 'month',    label: 'Month',    type: 'month',  required: true },
          { key: 'facility', label: 'Facility', type: 'select', dynamicSource: 'facilities' },
        ],
      },
      {
        id: '2.6', name: 'Facility Outbound By Service Area',
        excludeColumns: ['service_area_code', 'from_facility_code'],
        description: 'Outbound allocated tons by service area and destination facility.',
        quarterlyVariant: '2.6q',
        filters: [
          { key: 'year',            label: 'Year',          type: 'year',    required: true },
          { key: 'month',           label: 'Month',         type: 'month',   required: true, hideIfQuarterly: true },
          { key: 'period_quarter',  label: 'Quarter',       type: 'quarter', required: true, showIfQuarterly: true },
          { key: 'facility',        label: 'From Facility', type: 'select',  dynamicSource: 'facilities' },
          { key: 'to_facility_name',label: 'To Facility',   type: 'select',  dynamicSource: 'to_facilities' },
          { key: 'material_code',   label: 'Material Code', type: 'text',    placeholder: 'Optional' },
        ],
      },
      {
        id: '2.7', name: 'Facility Outbound Summary — All Materials',
        excludeColumns: ['from_facility_code', 'to_facility_code'],
        description: 'Total tons and revenue for all outbound materials by destination.',
        quarterlyVariant: '2.7q',
        filters: [
          { key: 'year',            label: 'Year',          type: 'year',    required: true },
          { key: 'month',           label: 'Month',         type: 'month',   required: true, hideIfQuarterly: true },
          { key: 'period_quarter',  label: 'Quarter',       type: 'quarter', required: true, showIfQuarterly: true },
          { key: 'facility',        label: 'From Facility', type: 'select',  dynamicSource: 'facilities' },
          { key: 'to_facility_name',label: 'To Facility',   type: 'select',  dynamicSource: 'to_facilities' },
        ],
      },
      {
        id: '2.8', name: 'Facility Outbound Summary — Recyclables and Organics',
        excludeColumns: ['from_facility_code', 'to_facility_code'],
        description: 'Outbound summary filtered to REC- and ORG- material codes only.',
        quarterlyVariant: '2.8q',
        filters: [
          { key: 'year',            label: 'Year',          type: 'year',    required: true },
          { key: 'month',           label: 'Month',         type: 'month',   required: true, hideIfQuarterly: true },
          { key: 'period_quarter',  label: 'Quarter',       type: 'quarter', required: true, showIfQuarterly: true },
          { key: 'facility',        label: 'From Facility', type: 'select',  dynamicSource: 'facilities' },
          { key: 'to_facility_name',label: 'To Facility',   type: 'select',  dynamicSource: 'to_facilities' },
        ],
      },
      {
        id: '2.9', name: 'Material Processing Summary',
        excludeColumns: ['Facility', 'Service Area'],
        description: 'Inbound and outbound tons by material processing method, with optional filtering by output type (disposal vs recovery).',
        filters: [
          { key: 'year',             label: 'Year',         type: 'year',    required: true },
          { key: 'month',            label: 'Month',        type: 'month',   required: true },
          { key: 'facility',         label: 'Facility',     type: 'select',  dynamicSource: 'facilities' },
          { key: 'service_area_name',label: 'Service Area', type: 'select',  dynamicSource: 'service_areas' },
          { key: 'output_type',      label: 'Output Type',  type: 'select',  staticOptions: [
            { value: 'disposal',  label: 'Disposal Only (DISP-)' },
            { value: 'recovery',  label: 'Recovery Only (non-DISP)' },
          ]},
        ],
      },
      {
        id: '2.10', name: 'Facility Load Type Summary — By Quarter',
        description: 'Total tons by load type and facility for a given quarter.',
        filters: [
          { key: 'year',           label: 'Year',     type: 'year',    required: true },
          { key: 'period_quarter', label: 'Quarter',  type: 'quarter', required: true },
          { key: 'facility',       label: 'Facility', type: 'select',  dynamicSource: 'facilities' },
        ],
      },
      {
        id: '2.11', name: 'RDRS Collection Methods Summary',
        description: 'Quarterly CalRecycle RDRS reporting by collection method table and category. Rows flagged PENDING CLASSIFICATION need attention before filing.',
        filters: [
          { key: 'year',           label: 'Year',     type: 'year',    required: true },
          { key: 'quarter',        label: 'Quarter',  type: 'quarter', required: true },
          { key: 'facility',       label: 'Facility', type: 'select',  dynamicSource: 'facilities', required: true },
        ],
      },
    ],
  },
  {
    id: '3', label: 'Facility Customer Reports and Summaries', color: 'purple',
    reports: [
      {
        id: '3.1', name: 'Facility Inbound Summary By Customer',
        description: 'Total tons and fees per customer per facility for a given month.',
        filters: [
          { key: 'year',            label: 'Year',            type: 'year',   required: true },
          { key: 'month',           label: 'Month',           type: 'month',  required: true },
          { key: 'facility',        label: 'Facility',        type: 'select', dynamicSource: 'facilities' },
          { key: 'customer_number', label: 'Customer Number', type: 'text',   placeholder: 'Optional' },
        ],
      },
      {
        id: '3.2', name: 'Customer Inbound Summary By Facility, Customer and Service Area',
        description: 'Detailed breakdown of customer tons and fees by service area and material.',
        excludeColumns: ['material_description'],
        filters: [
          { key: 'year',            label: 'Year',            type: 'year',   required: true },
          { key: 'month',           label: 'Month',           type: 'month',  required: true },
          { key: 'facility',        label: 'Facility',        type: 'select', dynamicSource: 'facilities' },
          { key: 'customer_number', label: 'Customer Number', type: 'text',   placeholder: 'Optional' },
          { key: 'material_type',   label: 'Material Type',   type: 'text',   placeholder: 'Optional' },
          { key: 'material_group',  label: 'Material Group',  type: 'text',   placeholder: 'Optional' },
        ],
      },
      {
        id: '3.3', name: 'Customer Tons and Fees By Period',
        description: 'Every ticket counted once — includes Transfer-type tickets and their fees, unlike the aggregated summary tables. Best source for transfer-related fees.',
        filters: [
          { key: 'year',            label: 'Year',            type: 'year',   required: true },
          { key: 'month',           label: 'Month',           type: 'month',  required: true },
          { key: 'facility',        label: 'Facility',        type: 'select', dynamicSource: 'facilities', required: true },
          { key: 'customer_number', label: 'Customer Number', type: 'text',   placeholder: 'Optional' },
        ],
      },
      {
        id: '3.4', name: 'Service Area Tons By Customer',
        description: 'What haulers brought loads from a service area, and what materials — one row per customer.',
        filters: [
          { key: 'service_area_code', label: 'Service Area', type: 'select',      dynamicSource: 'service_areas', required: true },
          { key: 'facility',          label: 'Facility',     type: 'select',      dynamicSource: 'facilities' },
          { key: 'period_type',       label: 'Period Type',  type: 'period_type' },
          { key: 'year',              label: 'Year',         type: 'year',    required: true },
          { key: 'month',             label: 'Month',        type: 'month',   showIfEquals: { key: 'period_type', value: 'month' } },
          { key: 'quarter',           label: 'Quarter',      type: 'quarter', showIfEquals: { key: 'period_type', value: 'quarter' } },
        ],
      },
      {
        id: '3.5', name: 'Customer Recovery Allocation',
        description: 'For a customer\'s inbound material, shows how much fed into facility recovery and their proportional share of it. Recovery only — a customer whose material was 100% straight disposal correctly shows no rows.',
        emptyMessage: 'No recovery activity for this selection — this can mean the material went straight to disposal, which is expected, not an error.',
        filters: [
          { key: 'facility',          label: 'Facility',        type: 'select', dynamicSource: 'facilities', required: true },
          { key: 'year',              label: 'Year',            type: 'year',   required: true },
          { key: 'month',             label: 'Month',           type: 'month',  required: true },
          { key: 'customer_number',   label: 'Customer Number', type: 'text',   placeholder: 'Optional' },
          { key: 'service_area_code', label: 'Service Area',    type: 'select', dynamicSource: 'service_areas' },
        ],
      },
      {
        id: '3.6', name: 'Customer Material Recovery',
        description: 'Recovery and disposal (as residual) side by side for one customer\'s inbound material — closes the gap Customer Recovery Allocation has on its own.',
        filters: [
          { key: 'facility',          label: 'Facility',        type: 'select', dynamicSource: 'facilities', required: true },
          { key: 'year',              label: 'Year',            type: 'year',   required: true },
          { key: 'month',             label: 'Month',           type: 'month',  required: true },
          { key: 'customer_number',   label: 'Customer Number', type: 'text',   placeholder: 'Required', required: true },
          { key: 'service_area_code', label: 'Service Area',    type: 'select', dynamicSource: 'service_areas' },
        ],
      },
    ],
  },
  {
    id: '4', label: 'Service Area Reports', color: 'orange',
    reports: [
      {
        id: '4.1', name: 'Inbound By Service Area',
        description: 'Inbound tons per service area and material for a given month.',
        filters: [
          { key: 'year',              label: 'Year',         type: 'year',   required: true },
          { key: 'month',             label: 'Month',        type: 'month',  required: true },
          { key: 'facility',          label: 'Facility',     type: 'select', dynamicSource: 'facilities' },
          { key: 'service_area_name', label: 'Service Area', type: 'select', dynamicSource: 'service_areas' },
        ],
      },
      {
        id: '4.2', name: 'Outbound By Service Area — All Company Facilities',
        description: 'Outbound tons by service area across all facilities.',
        filters: [
          { key: 'year',              label: 'Year',         type: 'year',   required: true },
          { key: 'month',             label: 'Month',        type: 'month',  required: true },
          { key: 'service_area_name', label: 'Service Area', type: 'select', dynamicSource: 'service_areas' },
        ],
      },
      {
        id: '4.3', name: 'Inbound Material Summary By Service Area',
        description: 'Pivot table — rows: service areas, columns: material codes, values: tons.',
        pivot: 'material_code',
        quarterlyVariant: '4.3q',
        filters: [
          { key: 'year',           label: 'Year',     type: 'year',    required: true },
          { key: 'month',          label: 'Month',    type: 'month',   required: true, hideIfQuarterly: true },
          { key: 'period_quarter', label: 'Quarter',  type: 'quarter', required: true, showIfQuarterly: true },
          { key: 'facility',       label: 'Facility', type: 'select',  dynamicSource: 'facilities' },
        ],
      },
      {
        id: '4.4', name: 'Outbound Disposal By Service Area and Facility',
        description: 'Pivot table — rows: service areas, columns: disposal facilities, values: tons.',
        pivot: 'facility_name',
        quarterlyVariant: '4.4q',
        filters: [
          { key: 'year',              label: 'Year',         type: 'year',    required: true },
          { key: 'month',             label: 'Month',        type: 'month',   required: true, hideIfQuarterly: true },
          { key: 'period_quarter',    label: 'Quarter',      type: 'quarter', required: true, showIfQuarterly: true },
          { key: 'facility',          label: 'Facility',     type: 'select',  dynamicSource: 'facilities' },
          { key: 'service_area_name', label: 'Service Area', type: 'select',  dynamicSource: 'service_areas' },
        ],
      },
      {
        id: '4.5', name: 'Service Area Performance Report',
        description: 'Recovered vs. residual tons by material, split using each recovered commodity\'s own material group — by facility, or company-wide across a service area.',
        filters: [
          { key: 'service_area_code', label: 'Service Area', type: 'select',      dynamicSource: 'service_areas', required: true },
          { key: 'facility',          label: 'Facility',     type: 'select',      dynamicSource: 'facilities' },
          { key: 'period_type',       label: 'Period Type',  type: 'period_type' },
          { key: 'year',              label: 'Year',         type: 'year',    required: true },
          { key: 'month',             label: 'Month',        type: 'month',   showIfEquals: { key: 'period_type', value: 'month' } },
          { key: 'quarter',           label: 'Quarter',      type: 'quarter', showIfEquals: { key: 'period_type', value: 'quarter' } },
        ],
      },
      {
        id: '4.6', name: 'Service Area Diversion Summary',
        description: 'Tons and diversion/disposal rates by service area and collection type. Rates are always recomputed from summed tons, never averaged across months.',
        filters: [
          { key: 'service_area_code', label: 'Service Area',   type: 'select',      dynamicSource: 'service_areas' },
          { key: 'collection_type',   label: 'Collection Type', type: 'select',     staticOptions: [{ value: 'Company', label: 'Company' }, { value: 'Independent', label: 'Independent' }] },
          { key: 'period_type',       label: 'Period Type',    type: 'period_type' },
          { key: 'year',              label: 'Year',           type: 'year',    required: true },
          { key: 'month',             label: 'Month',          type: 'month',   showIfEquals: { key: 'period_type', value: 'month' } },
          { key: 'quarter',           label: 'Quarter',        type: 'quarter', showIfEquals: { key: 'period_type', value: 'quarter' } },
        ],
      },
      {
        id: '4.7', name: 'Material Processing Summary',
        description: 'Inbound and outbound tons by material processing method for one facility, at month, quarter, or year grain.',
        filters: [
          { key: 'facility',          label: 'Facility',      type: 'select', dynamicSource: 'facilities', required: true },
          { key: 'service_area_code', label: 'Service Area',  type: 'select', dynamicSource: 'service_areas' },
          { key: 'period_type',       label: 'Period Type',   type: 'period_type' },
          { key: 'year',              label: 'Year',          type: 'year',    required: true },
          { key: 'month',             label: 'Month',         type: 'month',   showIfEquals: { key: 'period_type', value: 'month' } },
          { key: 'quarter',           label: 'Quarter',       type: 'quarter', showIfEquals: { key: 'period_type', value: 'quarter' } },
        ],
      },
      {
        id: '4.8', name: 'Monthly Material Summary',
        description: 'Row-level material detail for one facility, meant for extracting your own summaries — not pre-aggregated. Month only, by design.',
        rowLimitSelector: true,
        filters: [
          { key: 'facility',          label: 'Facility',      type: 'select', dynamicSource: 'facilities', required: true },
          { key: 'service_area_code', label: 'Service Area',  type: 'select', dynamicSource: 'service_areas' },
          { key: 'year',              label: 'Year',          type: 'year',   required: true },
          { key: 'month',             label: 'Month',         type: 'month',  required: true },
        ],
      },
      {
        id: '4.9', name: 'Inbound Material Trend',
        description: 'Inbound tons by material over a selectable trailing date range, for one facility. Toggle to Chart for a stacked column view by month.',
        trendChart: {
          periodYearKey:  'period_year',
          periodMonthKey: 'period_month',
          periodLabelKey: 'period_month_name',
          categoryKey:    'material_code',
          valueKey:       'total_tons',
        },
        filters: [
          { key: 'facility',          label: 'Facility',      type: 'select', dynamicSource: 'facilities', required: true },
          { key: 'service_area_code', label: 'Service Area',  type: 'select', dynamicSource: 'service_areas' },
          { key: 'date_range',        label: 'Date Range',    type: 'date_range_preset' },
        ],
      },
      {
        id: '4.10', name: 'Service Area Performance Trend',
        description: 'Recovered / Disposed / Reused tons over a selectable trailing date range, company-wide or for one service area. Land application folds into Reused. Toggle to Chart for a stacked column view by month.',
        trendChart: {
          periodYearKey:  'period_year',
          periodMonthKey: 'period_month',
          periodLabelKey: 'period_month_name',
          categoryKey:    'category',
          valueKey:       'tons',
          colors: { 'Recovered': '#16a34a', 'Disposed': '#dc2626', 'Reused': '#2563eb' },
        },
        filters: [
          { key: 'service_area_code', label: 'Service Area',  type: 'select', dynamicSource: 'service_areas' },
          { key: 'date_range',        label: 'Date Range',    type: 'date_range_preset' },
        ],
      },
    ],
  },
  {
    id: '5', label: 'Accounts, Services, and Billing Reports', color: 'pink',
    reports: [
      {
        id: '5.1', name: 'Account Services Summary',
        description: 'Active accounts with trash, recycle, and organics container and service details.',
        filters: [
          { key: 'account_number',       label: 'Account Number',    type: 'text',   placeholder: 'Optional' },
          { key: 'zone',                 label: 'Zone',               type: 'text',   placeholder: 'Optional' },
          { key: 'mf_class',             label: 'MF Class',           type: 'select', staticOptions: [{ value: 'Less Five', label: 'Less than 5 units' }, { value: 'Five Plus', label: '5+ units' }] },
          { key: 'service_area_name',    label: 'Service Area',       type: 'select', dynamicSource: 'service_areas' },
          { key: 'account_type',         label: 'Account Type',       type: 'select', staticOptions: [{ value: 'Commercial', label: 'Commercial' }, { value: 'Residential', label: 'Residential' }] },
          { key: 'business_type',        label: 'Business Type',      type: 'text',   placeholder: 'Optional' },
          { key: 'service_street_name',  label: 'Street Name',        type: 'text',   placeholder: 'Optional — partial match' },
          { key: 'min_weekly_yds',       label: 'Min Weekly Yards',   type: 'text',   placeholder: 'Optional' },
          { key: 'max_weekly_yds',       label: 'Max Weekly Yards',   type: 'text',   placeholder: 'Optional' },
          { key: 'no_recycling', label: 'No Recycling Service', type: 'select', staticOptions: [{ value: 'true', label: 'No Recycling Service' }] },
          { key: 'no_organics',   label: 'No Organics Service',   type: 'select', staticOptions: [{ value: 'true', label: 'No Organics Service' }] },
        ],
      },
      {
        id: '5.2', name: 'Period Billing Summary',
        description: 'Per-account billing summary for a period — account-level fields consolidated, varying line-item values preserved, charges summed, unit rate averaged.',
        quarterlyVariant: '5.2q',
        filters: [
          { key: 'year',              label: 'Year',          type: 'year',    required: true },
          { key: 'month',             label: 'Month',         type: 'month',   required: true, hideIfQuarterly: true },
          { key: 'period_quarter',    label: 'Quarter',       type: 'quarter', required: true, showIfQuarterly: true },
          { key: 'service_area_name', label: 'Service Area',  type: 'select',  dynamicSource: 'service_areas' },
        ],
      },
    ],
  },
];

// ─── Color maps ───────────────────────────────────────────────────────────────

const CAT_COLORS: Record<string, { bg: string; text: string; badge: string; btn: string; ring: string; toggle: string }> = {
  blue:   { bg: 'bg-blue-50',   text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-700',    btn: 'bg-blue-600 hover:bg-blue-700',    ring: 'focus:ring-blue-500',   toggle: 'bg-blue-600'   },
  green:  { bg: 'bg-green-50',  text: 'text-green-700',  badge: 'bg-green-100 text-green-700',   btn: 'bg-green-600 hover:bg-green-700',   ring: 'focus:ring-green-500',  toggle: 'bg-green-600'  },
  purple: { bg: 'bg-purple-50', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700', btn: 'bg-purple-600 hover:bg-purple-700', ring: 'focus:ring-purple-500', toggle: 'bg-purple-600' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-700', btn: 'bg-orange-600 hover:bg-orange-700', ring: 'focus:ring-orange-500', toggle: 'bg-orange-600' },
  pink:   { bg: 'bg-pink-50',   text: 'text-pink-700',   badge: 'bg-pink-100 text-pink-700',    btn: 'bg-pink-600 hover:bg-pink-700',    ring: 'focus:ring-pink-500',   toggle: 'bg-pink-600'   },
};

const CHART_COLORS = [
  '#2563eb', '#16a34a', '#9333ea', '#ea580c',
  '#0891b2', '#be185d', '#854d0e', '#166534',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toNum(s: string | undefined): number {
  if (!s) return 0;
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function exportCSV(data: Record<string, string>[], reportName: string, excludeColumns?: string[]) {
  if (!data.length) return;
  const headers = Object.keys(data[0]).filter(h => !excludeColumns?.includes(h));
  const rows    = data.map(r => headers.map(h => csvCell(r[h])).join(','));
  const csv     = [headers.map(csvCell).join(','), ...rows].join('\n');
  const blob    = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url     = URL.createObjectURL(blob);
  const a       = document.createElement('a');
  a.href        = url;
  a.download    = `${reportName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// Exports the PIVOTED matrix (what's actually shown on screen for 4.3/4.4)
// rather than the raw normalized rows returned by the API.
function exportPivotCSV(headers: string[], rows: Record<string, string>[], reportName: string) {
  if (!rows.length) return;
  const csvRows = rows.map(r => headers.map(h => csvCell(r[h])).join(','));
  const csv     = [headers.map(csvCell).join(','), ...csvRows].join('\n');
  const blob    = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url     = URL.createObjectURL(blob);
  const a       = document.createElement('a');
  a.href        = url;
  a.download    = `${reportName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-pivot.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// FIXED: rowDisplayKey ensures row[header] lookups work correctly in DataTable
function pivotData(
  data: Record<string, string>[],
  rowKey: string,
  colKey: string,
  valKey: string,
  rowDisplayKey: string = 'Service Area',
): { columns: string[]; rows: Record<string, string>[] } {
  const colSet = Array.from(new Set(data.map(r => r[colKey] ?? ''))).filter(Boolean).sort();
  const rowMap: Record<string, Record<string, string>> = {};

  data.forEach(r => {
    const rk = r[rowKey] ?? '';
    const ck = r[colKey] ?? '';
    if (!rowMap[rk]) rowMap[rk] = { [rowDisplayKey]: rk };
    rowMap[rk][ck] = String(toNum(r[valKey]) + toNum(rowMap[rk][ck]));
  });

  const rows = Object.values(rowMap).map(row => {
    let total = 0;
    colSet.forEach(c => { total += toNum(row[c]); });
    return { ...row, Total: total.toFixed(2) };
  });

  return { columns: colSet, rows };
}

function reshapeTrend(
  data: Record<string, string>[],
  cfg: TrendChartConfig
): { rows: Record<string, any>[]; categories: string[] } {
  const byPeriod: Record<string, any> = {};
  const categoriesSeen = new Set<string>();

  data.forEach(row => {
    const y     = row[cfg.periodYearKey];
    const m     = row[cfg.periodMonthKey];
    const label = row[cfg.periodLabelKey];
    const key   = `${y}-${String(m).padStart(2, '0')}`;
    if (!byPeriod[key]) byPeriod[key] = { period: `${label} ${y}`, sortKey: key };

    const cat = row[cfg.categoryKey];
    const val = toNum(row[cfg.valueKey]);
    byPeriod[key][cat] = toNum(byPeriod[key][cat]) + val;
    categoriesSeen.add(cat);
  });

  const rows = Object.values(byPeriod).sort((a: any, b: any) => a.sortKey.localeCompare(b.sortKey));
  const categories = Array.from(categoriesSeen).filter(Boolean).sort();
  return { rows, categories };
}

function printPivotTable(
  data: Record<string, string>[],
  pivot: ReportDef['pivot'],
  reportName: string,
  periodLabel: string,
) {
  if (!data.length) return;

  const colKey  = pivot === 'material_code' ? 'material_code' : 'facility_name';
  const rowKey  = 'service_area';
  const valKey  = pivot === 'material_code' ? 'total_tons' : 'disposal_tons';
  const { columns, rows } = pivotData(data, rowKey, colKey, valKey, 'Service Area');
  const headers = ['Service Area', ...columns, 'Total'];

  const tableRows = rows.map(r =>
    `<tr>${['Service Area', ...columns, 'Total'].map((k, i) =>
      `<td style="border:1px solid #ccc;padding:5px 8px;text-align:${i === 0 ? 'left' : 'right'}">${r[k] ?? '—'}</td>`
    ).join('')}</tr>`
  ).join('');

  const html = `<html><head><title>${reportName}</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:11px;margin:20px}
      h2{font-size:14px;margin-bottom:4px}
      p{font-size:11px;color:#666;margin-bottom:12px}
      table{border-collapse:collapse;width:100%}
      th{border:1px solid #ccc;padding:5px 8px;background:#f0f0f0;font-weight:bold;text-align:right}
      th:first-child{text-align:left}
      @media print{@page{size:landscape}}
    </style></head><body>
    <h2>${reportName}</h2><p>Period: ${periodLabel}</p>
    <table><thead><tr>${headers.map((h, i) =>
      `<th style="text-align:${i === 0 ? 'left' : 'right'}">${h}</th>`
    ).join('')}</tr></thead><tbody>${tableRows}</tbody></table>
  </body></html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 500);
}

// ─── FilterInput ──────────────────────────────────────────────────────────────

function FilterInput({
  def, value, onChange, colorKey, lookups,
}: {
  def: FilterDef;
  value: string;
  onChange: (v: string) => void;
  colorKey: string;
  lookups: Lookups;
}) {
  const ringClass = CAT_COLORS[colorKey]?.ring ?? 'focus:ring-blue-500';
  const base = `w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${ringClass}`;

  if (def.type === 'month') {
    return (
      <select value={value} onChange={e => onChange(e.target.value)} className={base}>
        {!def.required && <option value="">All Months</option>}
        {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
      </select>
    );
  }

  if (def.type === 'period_type') {
    return (
      <select value={value || 'month'} onChange={e => onChange(e.target.value)} className={base}>
        {PERIOD_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }

  if (def.type === 'date_range_preset') {
    return (
      <select value={value || 'trailing12'} onChange={e => onChange(e.target.value)} className={base}>
        {DATE_RANGE_PRESETS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }

  if (def.type === 'quarter') {
    return (
      <select value={value} onChange={e => onChange(e.target.value)} className={base}>
        {!def.required && <option value="">All Quarters</option>}
        {QUARTERS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
      </select>
    );
  }

  if (def.type === 'select' && def.dynamicSource) {
    let options: { value: string; label: string }[] = [];
    if (def.dynamicSource === 'facilities') {
      options = lookups.facilities.map(f => ({ value: f.facility_code, label: f.facility_name || f.facility_code }));
    } else if (def.dynamicSource === 'to_facilities') {
      options = lookups.facilities.map(f => ({ value: f.facility_name, label: f.facility_name || f.facility_code }));
    } else if (def.dynamicSource === 'service_areas') {
      // Some reports filter by service_area_code (e.g. 'LA'), others by
      // service_area_name (e.g. 'Los Angeles') — send whichever the filter
      // key itself asks for, so the value always matches what the Lambda
      // actually queries against.
      const useCode = def.key === 'service_area_code';
      options = lookups.serviceAreas.map(sa => ({
        value: useCode ? sa.service_area_code : sa.service_area_name,
        label: sa.service_area_name || sa.service_area_code,
      }));
    }
    return (
      <select value={value} onChange={e => onChange(e.target.value)} className={base}>
        <option value="">All</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }

  if (def.type === 'select' && def.staticOptions) {
    return (
      <select value={value} onChange={e => onChange(e.target.value)} className={base}>
        <option value="">All</option>
        {def.staticOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }

  return (
    <input
      type={def.type === 'year' ? 'number' : 'text'}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={def.placeholder}
      min={def.type === 'year' ? 2020 : undefined}
      max={def.type === 'year' ? Number(CURRENT_YEAR) : undefined}
      className={base}
    />
  );
}

// ─── DataTable ────────────────────────────────────────────────────────────────

function DataTable({
  data, pivot, excludeColumns,
}: {
  data: Record<string, string>[];
  pivot?: ReportDef['pivot'];
  excludeColumns?: string[];
}) {
  const display = useMemo(() => {
    if (!data.length) return { headers: [], rows: [] };

    // FIXED: pass rowDisplayKey so row[header] lookups resolve correctly on screen
    if (pivot === 'material_code') {
      const { columns, rows } = pivotData(data, 'service_area', 'material_code', 'total_tons', 'Service Area');
      return { headers: ['Service Area', ...columns, 'Total'], rows };
    }
    if (pivot === 'facility_name') {
      const { columns, rows } = pivotData(data, 'service_area', 'facility_name', 'disposal_tons', 'Service Area');
      return { headers: ['Service Area', ...columns, 'Total'], rows };
    }

    const headers = Object.keys(data[0]).filter(h => !excludeColumns?.includes(h));
    return { headers, rows: data };
  }, [data, pivot, excludeColumns]);

  if (!display.rows.length) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {display.headers.map(h => (
              <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                {h.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {display.rows.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50 transition-colors">
              {display.headers.map(h => {
                const cellValue = row[h] ?? '—';
                const isPending = typeof cellValue === 'string' && cellValue.startsWith('PENDING CLASSIFICATION');
                return (
                  <td key={h} className={`px-3 py-2 whitespace-nowrap text-xs ${isPending ? 'bg-amber-50' : ''}`}>
                    {isPending ? (
                      <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                        {cellValue}
                      </span>
                    ) : (
                      <span className="text-gray-700">{cellValue}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── TrendTable / TrendChart ───────────────────────────────────────────────────
// Distinct from the service-area pivot used by 4.3/4.4 — rows here are periods
// in chronological order, not an alphabetically sorted dimension, and the shape
// feeds a stacked chart as well as a table.

function TrendTable({ data, cfg }: { data: Record<string, string>[]; cfg: TrendChartConfig }) {
  const { rows, categories } = useMemo(() => reshapeTrend(data, cfg), [data, cfg]);
  if (!rows.length) return null;

  const headers = ['Period', ...categories, 'Total'];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {headers.map(h => (
              <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, i) => {
            const total = categories.reduce((s, c) => s + toNum(row[c]), 0);
            return (
              <tr key={i} className="hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2 text-gray-900 font-medium whitespace-nowrap text-xs">{row.period}</td>
                {categories.map(c => (
                  <td key={c} className="px-3 py-2 text-right text-gray-700 tabular-nums text-xs">
                    {toNum(row[c]).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                ))}
                <td className="px-3 py-2 text-right text-gray-900 font-semibold tabular-nums text-xs">
                  {total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TrendChart({ data, cfg }: { data: Record<string, string>[]; cfg: TrendChartConfig }) {
  const { rows, categories } = useMemo(() => reshapeTrend(data, cfg), [data, cfg]);
  if (!rows.length) return null;

  return (
    <div className="p-5">
      <ResponsiveContainer width="100%" height={340}>
        <BarChart data={rows} margin={{ top: 10, right: 20, left: 0, bottom: 50 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="period" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v: any) => (typeof v === 'number' ? v.toLocaleString() : v)} />
          <Legend verticalAlign="top" align="center" wrapperStyle={{ fontSize: 12, paddingBottom: 12 }} />
          {categories.map((cat, i) => (
            <Bar key={cat} dataKey={cat} stackId="a" fill={cfg.colors?.[cat] ?? CHART_COLORS[i % CHART_COLORS.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── ReportCard ───────────────────────────────────────────────────────────────

function ReportCard({
  report, colorKey, apiUrl, getToken, lookups,
}: {
  report: ReportDef;
  colorKey: string;
  apiUrl: string;
  getToken: () => Promise<string>;
  lookups: Lookups;
}) {
  const colors = CAT_COLORS[colorKey] ?? CAT_COLORS.blue;

  const defaultFilters: Record<string, string> = {};
  report.filters.forEach(f => {
    if (f.type === 'year')    defaultFilters[f.key] = CURRENT_YEAR;
    if (f.type === 'month')   defaultFilters[f.key] = CURRENT_MONTH;
    if (f.type === 'quarter') defaultFilters[f.key] = CURRENT_Q;
    if (f.type === 'period_type')       defaultFilters[f.key] = 'month';
    if (f.type === 'date_range_preset') defaultFilters[f.key] = 'trailing12';
  });

  const [expanded,    setExpanded]    = useState(false);
  const [isQuarterly, setIsQuarterly] = useState(false);
  const [filters,     setFilters]     = useState<Record<string, string>>(defaultFilters);
  const [rowLimit,    setRowLimit]    = useState('1000');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [data,        setData]        = useState<Record<string, string>[]>([]);
  const [truncated,   setTruncated]   = useState(false);
  const [hasRun,      setHasRun]      = useState(false);
  const [viewMode,    setViewMode]    = useState<'table' | 'chart'>('table');

  const activeReportId = isQuarterly && report.quarterlyVariant ? report.quarterlyVariant : report.id;

  const activeFilters = report.filters.filter(f => {
    if (isQuarterly  && f.hideIfQuarterly) return false;
    if (!isQuarterly && f.showIfQuarterly) return false;
    if (f.showIfEquals && filters[f.showIfEquals.key] !== f.showIfEquals.value) return false;
    return true;
  });

  const missingRequired = activeFilters
    .filter(f => f.required && !filters[f.key])
    .map(f => f.label);

  const periodLabel = useMemo(() => {
    const y = filters.year || '';
    if (isQuarterly) return `${y} ${QUARTERS.find(q => q.value === filters.period_quarter)?.label ?? ''}`;
    return `${MONTHS.find(m => m.value === filters.month)?.label ?? ''} ${y}`;
  }, [filters, isQuarterly]);

  const runReport = async () => {
    if (missingRequired.length) return;
    setLoading(true);
    setError(null);
    try {
      const token  = await getToken();
      const params: Record<string, string> = { ...filters };
      if (report.rowLimitSelector) params.row_limit = rowLimit;

      // date_range_preset filters compute a start/end range client-side and
      // inject those four params instead of sending the preset string itself
      report.filters.forEach(f => {
        if (f.type === 'date_range_preset') {
          const range = computeDateRange(filters[f.key] || 'trailing12');
          delete params[f.key];
          Object.assign(params, range);
        }
      });

      const res = await fetch(`${apiUrl}/reports/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_id: activeReportId, params }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error ?? `HTTP ${res.status}`);
      }
      const result = await res.json();
      setData(result.data ?? []);
      setTruncated(result.truncated ?? false);
      setHasRun(true);
    } catch (e: any) {
      setError(e.message ?? 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setExpanded(x => !x)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colors.badge}`}>{report.id}</span>
          <span className="text-sm font-semibold text-gray-900">{report.name}</span>
          {report.quarterlyVariant && (
            <span className="text-xs text-gray-400 flex items-center space-x-1">
              <Calendar className="w-3 h-3" /><span>Monthly / Quarterly</span>
            </span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          <div className={`px-5 py-3 ${colors.bg}`}>
            <p className="text-xs text-gray-600">{report.description}</p>
          </div>

          {report.quarterlyVariant && (
            <div className="px-5 pt-4 flex items-center space-x-3">
              <span className={`text-xs font-medium ${!isQuarterly ? 'text-gray-900' : 'text-gray-400'}`}>Monthly</span>
              <button
                onClick={() => setIsQuarterly(x => !x)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isQuarterly ? colors.toggle : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${isQuarterly ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
              <span className={`text-xs font-medium ${isQuarterly ? 'text-gray-900' : 'text-gray-400'}`}>Quarterly</span>
            </div>
          )}

          <div className="px-5 py-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {activeFilters.map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {f.label} {f.required && <span className="text-red-500">*</span>}
                  </label>
                  <FilterInput
                    def={f} value={filters[f.key] ?? ''}
                    onChange={v => setFilters(x => ({ ...x, [f.key]: v }))}
                    colorKey={colorKey} lookups={lookups}
                  />
                </div>
              ))}
              {report.rowLimitSelector && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Max Rows</label>
                  <select value={rowLimit} onChange={e => setRowLimit(e.target.value)}
                    className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${colors.ring}`}>
                    {ROW_LIMIT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              )}
            </div>

            {report.rowLimitSelector && Number(rowLimit) > 1000 && (
              <div className="flex items-start space-x-2 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-yellow-700">Requesting more than 1,000 rows may result in slower load times.</p>
              </div>
            )}

            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center space-x-2">
                <button onClick={runReport} disabled={loading || missingRequired.length > 0}
                  className={`flex items-center space-x-2 px-4 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${colors.btn}`}>
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  <span>{loading ? 'Running…' : 'Run Report'}</span>
                </button>
                {hasRun && data.length > 0 && report.trendChart && (
                  <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
                    >
                      Table
                    </button>
                    <button
                      onClick={() => setViewMode('chart')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'chart' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
                    >
                      Chart
                    </button>
                  </div>
                )}
					
			
                {hasRun && data.length > 0 && (
                  <button
                    onClick={() => {
                      if (report.trendChart) {
                        const { rows, categories } = reshapeTrend(data, report.trendChart);
                        exportPivotCSV(['Period', ...categories, 'Total'], rows.map(r => ({
                          ...r,
                          Period: r.period,
                          Total: categories.reduce((s, c) => s + toNum(r[c]), 0).toFixed(2),
                        })), report.name);
                      } else if (report.pivot) {
                        const colKey = report.pivot === 'material_code' ? 'material_code' : 'facility_name';
                        const valKey = report.pivot === 'material_code' ? 'total_tons' : 'disposal_tons';
                        const { columns, rows } = pivotData(data, 'service_area', colKey, valKey, 'Service Area');
                        exportPivotCSV(['Service Area', ...columns, 'Total'], rows, report.name);
                      } else {
                        exportCSV(data, report.name, report.excludeColumns);
                      }
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors">
                    <Download className="w-4 h-4" /><span>Export CSV</span>
                  </button>
                )}
                {hasRun && data.length > 0 && (
                  <ExportButtons
                    reportTitle={report.name}
                    getExportData={() => {
                      if (report.trendChart) {
                        const { rows, categories } = reshapeTrend(data, report.trendChart);
                        return {
                          columns: ['Period', ...categories, 'Total'],
                          data: rows.map(r => ({
                            ...r,
                            Period: r.period,
                            Total: categories.reduce((s, c) => s + toNum(r[c]), 0).toFixed(2),
                          })),
                        };
                      } else if (report.pivot) {
                        const colKey = report.pivot === 'material_code' ? 'material_code' : 'facility_name';
                        const valKey = report.pivot === 'material_code' ? 'total_tons' : 'disposal_tons';
                        const { columns, rows } = pivotData(data, 'service_area', colKey, valKey, 'Service Area');
                        return { columns: ['Service Area', ...columns, 'Total'], data: rows };
                      } else {
                        const cols = Object.keys(data[0]).filter(h => !report.excludeColumns?.includes(h));
                        return { columns: cols, data };
                      }
                    }}
                  />
                )}
              </div>
              {missingRequired.length > 0 && (
                <p className="text-xs text-red-500">Required: {missingRequired.join(', ')}</p>
              )}
            </div>
          </div>

          {error && (
            <div className="mx-5 mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-red-800">Failed to run report</p>
                <p className="text-xs text-red-600 mt-0.5">{error}</p>
              </div>
              <button onClick={() => setError(null)}><X className="w-4 h-4 text-red-400" /></button>
            </div>
          )}

          {truncated && (
            <div className="mx-5 mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
              <p className="text-xs text-yellow-700">
                Results are limited to {Number(rowLimit).toLocaleString()} rows. Increase the limit or add filters to see more.
              </p>
            </div>
          )}

          {hasRun && (
            <div className="border-t border-gray-100">
              {data.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-25" />
                  <p className="text-sm">{report.emptyMessage ?? 'No data found for the selected filters.'}</p>
                </div>
              ) : (
                <>
                  <div className="px-5 py-2 bg-gray-50 border-b border-gray-100">
                    <span className="text-xs text-gray-500">{data.length.toLocaleString()} row{data.length !== 1 ? 's' : ''}</span>
                  </div>
                  {report.trendChart && viewMode === 'chart' ? (
                    <TrendChart data={data} cfg={report.trendChart} />
                  ) : report.trendChart ? (
                    <TrendTable data={data} cfg={report.trendChart} />
                  ) : (
                    <DataTable data={data} pivot={report.pivot} excludeColumns={report.excludeColumns} />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CategorySection ──────────────────────────────────────────────────────────

function CategorySection({
  category, apiUrl, getToken, lookups,
}: {
  category: Category; apiUrl: string; getToken: () => Promise<string>; lookups: Lookups;
}) {
  const [open, setOpen] = useState(true);
  const colors = CAT_COLORS[category.color] ?? CAT_COLORS.blue;

  return (
    <div className="space-y-3">
      <button onClick={() => setOpen(x => !x)} className="w-full flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {open ? <ChevronDown className={`w-5 h-5 ${colors.text}`} />
                : <ChevronRight className={`w-5 h-5 ${colors.text}`} />}
          <h2 className={`text-base font-bold ${colors.text}`}>{category.label}</h2>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors.badge}`}>
            {category.reports.length} report{category.reports.length !== 1 ? 's' : ''}
          </span>
        </div>
      </button>
      {open && (
        <div className="space-y-2 pl-2">
          {category.reports.map(report => (
            <ReportCard key={report.id} report={report} colorKey={category.color}
              apiUrl={apiUrl} getToken={getToken} lookups={lookups} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ReportsLibrary() {
  const { getToken } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL ?? '';

  const [lookups,     setLookups]     = useState<Lookups>({ facilities: [], serviceAreas: [] });
  const [lookupError, setLookupError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token   = await getToken();
        const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
        const [facRes, saRes] = await Promise.all([
          fetch(`${API_URL}/reports/generate`, {
            method: 'POST', headers,
            body: JSON.stringify({ report_id: 'lookup.facilities', params: {} }),
          }),
          fetch(`${API_URL}/reports/generate`, {
            method: 'POST', headers,
            body: JSON.stringify({ report_id: 'lookup.service_areas', params: {} }),
          }),
        ]);
        const facData = await facRes.json();
        const saData  = await saRes.json();
        setLookups({ facilities: facData.data ?? [], serviceAreas: saData.data ?? [] });
      } catch (e: any) {
        console.error('Failed to load lookups:', e);
        setLookupError(e.message ?? 'Failed to load filter options');
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-gray-800 rounded-lg">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Reports Library</h1>
            <p className="text-sm text-gray-500">
              {CATEGORIES.reduce((s, c) => s + c.reports.length, 0)} pre-formatted reports across {CATEGORIES.length} categories
            </p>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
        {lookupError && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-yellow-800">Filter dropdowns failed to load</p>
              <p className="text-sm text-yellow-600 mt-0.5">{lookupError}</p>
            </div>
          </div>
        )}
        {CATEGORIES.map(cat => (
          <CategorySection key={cat.id} category={cat} apiUrl={API_URL}
            getToken={getToken} lookups={lookups} />
        ))}
      </main>
    </div>
  );
}
